import { useEffect, useState } from "react";

/**
 * Recharts takes literal color strings (fill/stroke), not Tailwind classes,
 * so chart colors can't be tokenized via className the way the rest of the
 * UI is. Instead we read the same CSS custom properties the design tokens
 * are built from (see src/index.css) at mount time, and fall back to the
 * literal values baked into :root so charts still render correctly before
 * the DOM is available (or if a var is ever missing).
 */
const FALLBACK_HSL = {
  primary: "142 71% 45%",
  success: "160 84% 39%",
  warning: "38 92% 50%",
  info: "217 91% 60%",
  accent: "142 71% 45%",
  mutedForeground: "215 16% 47%",
  channelMessenger: "221 83% 53%",
  channelInstagram: "339 90% 51%",
  channelWhatsapp: "142 71% 45%",
  channelWebsite: "215 16% 47%",
} as const;

type ChartColorKey = keyof typeof FALLBACK_HSL;

const CSS_VAR_MAP: Record<ChartColorKey, string> = {
  primary: "--primary",
  success: "--success",
  warning: "--warning",
  info: "--info",
  accent: "--accent",
  mutedForeground: "--muted-foreground",
  channelMessenger: "--channel-messenger",
  channelInstagram: "--channel-instagram",
  channelWhatsapp: "--channel-whatsapp",
  channelWebsite: "--channel-website",
};

function readHslVars(): Record<ChartColorKey, string> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return FALLBACK_HSL as unknown as Record<ChartColorKey, string>;
  }
  const styles = getComputedStyle(document.documentElement);
  const resolved = {} as Record<ChartColorKey, string>;
  (Object.keys(FALLBACK_HSL) as ChartColorKey[]).forEach((key) => {
    const raw = styles.getPropertyValue(CSS_VAR_MAP[key]).trim();
    resolved[key] = raw || FALLBACK_HSL[key];
  });
  return resolved;
}

function toHsl(channels: string, alpha?: number) {
  return alpha != null ? `hsl(${channels} / ${alpha})` : `hsl(${channels})`;
}

/** Brand chart colors, resolved from CSS custom properties on mount. */
export function useChartColors() {
  const [vars, setVars] = useState(readHslVars);

  useEffect(() => {
    setVars(readHslVars());
  }, []);

  return {
    primary: toHsl(vars.primary),
    success: toHsl(vars.success),
    warning: toHsl(vars.warning),
    info: toHsl(vars.info),
    accent: toHsl(vars.accent),
    mutedForeground: toHsl(vars.mutedForeground),
    primarySoft: toHsl(vars.primary, 0.12),
    successSoft: toHsl(vars.success, 0.18),
    channel: {
      messenger: toHsl(vars.channelMessenger),
      facebook: toHsl(vars.channelMessenger),
      instagram: toHsl(vars.channelInstagram),
      whatsapp: toHsl(vars.channelWhatsapp),
      website: toHsl(vars.channelWebsite),
    },
  };
}

/** Maps a platform label from API data to its brand channel color. */
export function platformColor(
  platform: string,
  colors: ReturnType<typeof useChartColors>,
  fallbackIndex: number
) {
  const key = platform.trim().toLowerCase();
  if (key.includes("facebook") || key.includes("messenger")) return colors.channel.messenger;
  if (key.includes("instagram")) return colors.channel.instagram;
  if (key.includes("whatsapp")) return colors.channel.whatsapp;
  if (key.includes("website") || key.includes("web chat")) return colors.channel.website;
  const fallbacks = [colors.primary, colors.success, colors.warning, colors.info];
  return fallbacks[fallbackIndex % fallbacks.length];
}
